namespace $ {

	export type $shm_hitalama_telemetry_sentry_context = {
		version: string
		user_id: string
		client_id: string
		map_id: string
		mode: string
		section: string
	}

	type $shm_hitalama_telemetry_sentry_pending = {
		node: Element
		started: number
		reports: number
		next_report: number
		notification_id?: string
	}

	type $shm_hitalama_telemetry_sentry_fiber = {
		id: string
		task: string
		host: string
		args: readonly unknown[]
		stack: string
		children: $shm_hitalama_telemetry_sentry_fiber[]
	}

	/** Browser error reporting and diagnostics for long-running $mol suspensions. */
	export class $shm_hitalama_telemetry_sentry extends $mol_object {

		private static views = new WeakMap< Element, $mol_view >()
		private static started = false
		private static observer: MutationObserver | null = null
		private static timer: ReturnType< typeof setInterval > | null = null
		private static pending = new Map< Element, $shm_hitalama_telemetry_sentry_pending >()
		private static view_errors = new WeakMap< Element, string >()
		private static watched_promises = new WeakMap< object, { timer: ReturnType< typeof setTimeout >, notification_id: string } >()
		private static recent = new Map< string, number >()
		private static sent_times: number[] = []
		private static suppressed = new Map< string, number >()
		private static capture_pending = new Map< string, { error: Error, area: string, data: Record< string, unknown > } >()
		private static context: $shm_hitalama_telemetry_sentry_context = {
			version: '-',
			user_id: '',
			client_id: '',
			map_id: '',
			mode: 'web',
			section: '',
		}

		static sdk() {
			return ( this.$.$mol_dom_context as any ).Sentry as any
		}

		static id( source: string ) {
			let hash = 2166136261
			for( let index = 0; index < source.length; ++index ) {
				hash ^= source.charCodeAt( index )
				hash = Math.imul( hash, 16777619 )
			}
			return ( hash >>> 0 ).toString( 36 )
		}

		static view_probe_install() {
			const proto = this.$.$mol_view.prototype as any
			const marker = '$' + 'shm_hitalama_telemetry_sentry_dom_node_external'
			if( proto[ marker ] ) return
			const dom_node_external = proto.dom_node_external
			proto.dom_node_external = function( this: $mol_view, next?: Element ) {
				const node = dom_node_external.call( this, next ) as Element
				$shm_hitalama_telemetry_sentry.views.set( node, this )
				return node
			}
			proto[ marker ] = true
		}

		static fail_probe_install() {
			const context = this.$ as any
			const marker = '$' + 'shm_hitalama_telemetry_sentry_fail_native'
			if( context[ marker ] ) return
			const native = context.$mol_fail_log as ( error: unknown )=> boolean
			context[ marker ] = native
			context.$mol_fail_log = ( error: unknown )=> {
				if( error instanceof Error ) this.capture( error, 'mol_view' )
				return native( error )
			}
		}

		static integrations_safe( defaults: readonly any[] ) {
			// BrowserApiErrors wraps every DOM listener and Breadcrumbs subscribes to
			// user input globally. Both are particularly expensive on the map's drag path.
			return defaults.filter( integration => integration?.name !== 'BrowserApiErrors' && integration?.name !== 'Breadcrumbs' )
		}

		static dsn() {
			const document = this.$.$mol_dom_context.document
			return document?.querySelector< HTMLMetaElement >( 'meta[name="hitalama-sentry-dsn"]' )?.content.trim() ?? ''
		}

		static start() {
			if( this.started ) return
			this.started = true
			this.view_probe_install()
			this.fail_probe_install()
			this.sdk_start()
			this.runtime_guards_start()
			this.discarded_report()
			if( !this.sdk() ) this.$.$mol_dom_context.addEventListener?.(
				'hitalama-sentry-ready',
				()=> this.sdk_start(),
				{ once: true },
			)
			this.monitor_start()
		}

		static runtime_guards_start() {
			const context = this.$.$mol_dom_context
			context.addEventListener?.( 'error', ( event: ErrorEvent )=> {
				if( !event.error ) return
				this.capture( event.error, 'window.error' )
			} )
			context.addEventListener?.( 'unhandledrejection', ( event: PromiseRejectionEvent )=> {
				if( $mol_promise_like( event.reason ) ) return
				const error = event.reason instanceof Error ? event.reason : new Error( String( event.reason ) )
				this.capture( error, 'unhandledrejection' )
			} )
		}

		static discarded_report() {
			const document = this.$.$mol_dom_context.document as Document & { wasDiscarded?: boolean }
			if( !document?.wasDiscarded ) return
			const error = new Error( 'Browser discarded and reloaded the page' )
			this.capture( error, 'browser_discarded', {
				reason: 'memory_or_resource_pressure',
			} )
			;( this.$.$shm_hitalama_notifications as any ).show({
				id: 'hitalama_browser_discarded',
				type: 'warning',
				title: 'Вкладка была перезагружена браузером',
				message: 'Браузер выгрузил страницу из памяти. Проверьте незавершённый ввод.',
				details: `${ error.name }: ${ error.message }`,
				copy_text: error.stack || `${ error.name }: ${ error.message }`,
				timeout: 0,
			})
		}

		static sdk_start() {
			const sdk = this.sdk()
			const dsn = this.dsn()
			if( sdk?.init && dsn && !sdk.isInitialized?.() ) {
				sdk.init({
					dsn,
					environment: this.environment(),
					sendDefaultPii: false,
					integrations: ( defaults: readonly any[] )=> this.integrations_safe( defaults ),
					tracesSampleRate: 0,
					replaysSessionSampleRate: 0,
					replaysOnErrorSampleRate: 0,
					maxBreadcrumbs: 100,
					normalizeDepth: 6,
					beforeSend: ( event: any )=> this.event_enrich( event ),
				})
			}
			sdk?.addEventProcessor?.( ( event: any )=> this.event_enrich( event ) )
			this.context_apply( sdk )
			if( sdk?.captureException ) {
				const pending = [ ... this.capture_pending.values() ]
				this.capture_pending.clear()
				for( const item of pending ) this.capture( item.error, item.area, item.data )
			}
		}

		static capture( error: Error, area: string, data: Record< string, unknown > = {} ) {
			const signature = `caught:${ area }:${ error.name }:${ error.message }`
			const sdk = this.sdk()
			if( !sdk?.captureException ) {
				this.capture_pending.set( signature, { error, area, data } )
				if( this.capture_pending.size > 20 ) this.capture_pending.delete( this.capture_pending.keys().next().value! )
				return null
			}
			if( !this.send_allowed( signature ) ) return null
			const send = ( scope: any )=> {
				scope?.setFingerprint?.([ 'hitalama-caught', area, error.name, error.message ])
				scope?.setContext?.( 'hitalama_failure', { area, ... data } )
				sdk.captureException( error )
			}
			if( sdk.withScope ) sdk.withScope( send )
			else send( null )
			return true
		}

		static promise_watch( promise: PromiseLike< unknown >, context: string ) {
			const key = promise as object
			if( this.watched_promises.has( key ) ) return promise

			const notification_id = `hitalama_loading_${ this.id( context ) }`
			const timer = setTimeout( ()=> {
				const error = new Error( `Long data loading in ${ context }` )
				error.name = 'HitalamaLoadingStalledError'
				this.capture( error, 'promise_watch', { context } )
				;( this.$.$shm_hitalama_notifications as any ).show({
					id: notification_id,
					type: 'warning',
					title: 'Долгая загрузка',
					message: 'Данные загружаются дольше обычного. Остальные доступные разделы продолжают работать.',
					details: `${ error.name }: ${ error.message }`,
					copy_text: error.stack || `${ error.name }: ${ error.message }`,
					timeout: 0,
				})
			}, 10_000 )
			this.watched_promises.set( key, { timer, notification_id } )

			const finish = ()=> {
				const watched = this.watched_promises.get( key )
				if( !watched ) return
				clearTimeout( watched.timer )
				;( this.$.$shm_hitalama_notifications as any ).close( watched.notification_id )
				this.watched_promises.delete( key )
			}
			Promise.resolve( promise ).then( finish, finish )
			return promise
		}

		static environment() {
			const host = this.$.$mol_dom_context.location?.hostname ?? ''
			if( host === 'localhost' || host === '127.0.0.1' ) return 'development'
			if( host.includes( '158-160-195-195' ) ) return 'prerelease'
			return 'production'
		}

		static context_update( next: $shm_hitalama_telemetry_sentry_context ) {
			this.context = { ... next }
			this.context_apply()
		}

		static context_apply( sdk = this.sdk() ) {
			if( !sdk ) return
			const next = this.context
			sdk.setUser?.( next.user_id ? { id: next.user_id } : null )
			sdk.setTags?.({
				app_version: next.version,
				client_id: next.client_id,
				map_id: next.map_id,
				mode: next.mode,
				section: next.section,
			})
			sdk.setContext?.( 'hitalama', {
				... next,
				url: this.$.$mol_dom_context.location?.href ?? '',
			})
		}

		static event_enrich( event: any ) {
			const context = this.context
			if( context.version && context.version !== '-' ) event.release = `hitalama@${ context.version }`
			event.tags = {
				... event.tags,
				app_version: context.version,
				client_id: context.client_id,
				map_id: context.map_id,
				mode: context.mode,
				section: context.section,
			}
			return event
		}

		static monitor_start() {
			const document = this.$.$mol_dom_context.document
			if( !document?.documentElement || typeof MutationObserver === 'undefined' ) return
			this.scan( document.documentElement )
			this.observer = new MutationObserver( mutations => {
				for( const mutation of mutations ) {
					if( mutation.type === 'attributes' ) this.track( mutation.target as Element )
				}
			} )
			this.observer.observe( document.documentElement, {
				attributes: true,
				attributeFilter: [ 'mol_view_error' ],
				subtree: true,
			} )
			this.timer = setInterval( ()=> {
				// A suspended view can be inserted after its error attribute was set while
				// detached. A periodic native selector catches it without observing every
				// child-list mutation made by the map.
				this.scan( document.documentElement )
				this.check()
			}, 1000 )
		}

		static scan( root: Element ) {
			this.track( root )
			for( const node of root.querySelectorAll( '[mol_view_error]' ) ) {
				this.track( node )
			}
		}

		static track( node: Element ) {
			const error = node.getAttribute( 'mol_view_error' )
			const waiting = error === 'Promise' || error === '$mol_promise_blocker'
			const current = this.pending.get( node )
			if( waiting ) {
				if( !current ) {
					const now = Date.now()
					this.pending.set( node, { node, started: now, reports: 0, next_report: now + 10_000 } )
				}
				return
			}
			if( current ) {
				this.pending.delete( node )
				if( current.notification_id ) ( this.$.$shm_hitalama_notifications as any ).close( current.notification_id )
				if( current.reports ) this.sdk()?.addBreadcrumb?.({
					category: 'mol.promise',
					level: 'info',
					message: 'Suspension resolved',
					data: { duration_ms: Date.now() - current.started, view: this.node_name( node ) },
				})
			}

			if( !error ) {
				this.view_errors.delete( node )
				return
			}
			if( this.view_errors.get( node ) === error ) return
			this.view_errors.set( node, error )
			const view = this.views.get( node )
			const context = view ? this.safe_text( view ) : this.node_name( node )
			const area = context.includes( 'widget' ) ? 'widget' : 'application'
			const failure = new Error( `View rendering failed: ${ error }` )
			this.capture( failure, 'view', { area, context } )
		}

		static check( now = Date.now() ) {
			for( const [ node, wait ] of this.pending ) {
				if( !node.isConnected ) {
					this.pending.delete( node )
					if( wait.notification_id ) ( this.$.$shm_hitalama_notifications as any ).close( wait.notification_id )
					continue
				}
				if( now < wait.next_report ) continue
				if( this.$.$mol_dom_context.document?.visibilityState === 'hidden' ) continue
				this.promise_report( wait, now )
				wait.reports++
				const duration = now - wait.started
				wait.next_report = wait.started + ( duration < 30_000 ? 30_000 : duration < 120_000 ? 120_000 : duration + 120_000 )
			}
		}

		static promise_report( wait: $shm_hitalama_telemetry_sentry_pending, now = Date.now() ) {
			const snapshot = this.pending_snapshot( wait.node )
			const leaf = this.fiber_leaf( snapshot.fiber )
			const signature = leaf?.id || snapshot.view || wait.node.id || 'unknown'
			if( !wait.notification_id ) {
				wait.notification_id = `hitalama_loading_${ this.id( signature ) }`
				;( this.$.$shm_hitalama_notifications as any ).show({
					id: wait.notification_id,
					type: 'warning',
					title: 'Долгая загрузка',
					message: 'Данные загружаются дольше обычного. Остальные доступные разделы продолжают работать.',
					details: `MolPromiseStalledError: ${ signature }`,
					copy_text: `MolPromiseStalledError: ${ signature }`,
					timeout: 0,
				})
			}
			if( !this.send_allowed( signature, now ) ) {
				this.suppressed.set( signature, ( this.suppressed.get( signature ) ?? 0 ) + 1 )
				return
			}

			const duration = now - wait.started
			const error = new Error( `Long $mol Promise in ${ signature } (${ Math.round( duration / 1000 ) }s)` )
			error.name = 'MolPromiseStalledError'
			if( leaf?.stack ) error.stack = `${ error.name }: ${ error.message }\n${ leaf.stack }`

			const sdk = this.sdk()
			if( !sdk?.captureException ) return
			const send = ( scope: any )=> {
				scope?.setLevel?.( duration >= 30_000 ? 'error' : 'warning' )
				scope?.setFingerprint?.([ 'mol-promise-stalled', signature ])
				scope?.setContext?.( 'mol_promise', {
					duration_ms: duration,
					report_number: wait.reports + 1,
					suppressed_repeats: this.suppressed.get( signature ) ?? 0,
					view: snapshot.view,
					node_id: wait.node.id,
					pending_views: this.pending.size,
					fiber_chain: this.fiber_lines( snapshot.fiber ),
					fiber: snapshot.fiber,
					online: this.$.$mol_dom_context.navigator?.onLine ?? true,
					visibility: this.$.$mol_dom_context.document?.visibilityState ?? '',
					url: this.$.$mol_dom_context.location?.href ?? '',
				} )
				this.suppressed.delete( signature )
				sdk.captureException( error )
			}
			if( sdk.withScope ) sdk.withScope( send )
			else send( null )
		}

		static send_allowed( signature: string, now = Date.now() ) {
			const duplicate_window = 2_000
			if( now - ( this.recent.get( signature ) ?? 0 ) < duplicate_window ) return false
			for( const [ key, time ] of this.recent ) {
				if( now - time > 5 * 60_000 ) this.recent.delete( key )
			}
			if( this.suppressed.size > 500 ) this.suppressed.clear()
			this.sent_times = this.sent_times.filter( time => now - time < 60_000 )
			if( this.sent_times.length >= 100 ) return false
			this.recent.set( signature, now )
			this.sent_times.push( now )
			return true
		}

		static pending_snapshot( node: Element ) {
			const view = this.views.get( node ) ?? null
			const fiber = view ? Object.getOwnPropertyDescriptor( view, 'dom_tree()' )?.value : null
			return {
				view: view ? String( view ) : this.node_name( node ),
				fiber: this.fiber_snapshot( fiber, new Set, 0 ),
			}
		}

		static fiber_snapshot( fiber: any, seen: Set< any >, depth: number ): $shm_hitalama_telemetry_sentry_fiber | null {
			if( !fiber || seen.has( fiber ) || depth >= 12 ) return null
			seen.add( fiber )
			const children = [] as $shm_hitalama_telemetry_sentry_fiber[]
			const data = Array.isArray( fiber.data ) ? fiber.data : []
			for( let index = Number( fiber.pub_from ) || 0; index < ( Number( fiber.sub_from ) || 0 ); index += 2 ) {
				const child = data[ index ]
				if( !child || !this.$.$mol_promise_like( child.cache ) ) continue
				const snapshot = this.fiber_snapshot( child, seen, depth + 1 )
				if( snapshot ) children.push( snapshot )
			}
			return {
				id: this.safe_text( fiber ),
				task: String( fiber.task?.name ?? '' ),
				host: String( fiber.host?.constructor?.name ?? '' ),
				args: this.args_snapshot( fiber.args ),
				stack: fiber.cache?.stack ? String( fiber.cache.stack ) : '',
				children,
			}
		}

		static fiber_leaf( fiber: $shm_hitalama_telemetry_sentry_fiber | null ): $shm_hitalama_telemetry_sentry_fiber | null {
			if( !fiber ) return null
			return fiber.children.length ? this.fiber_leaf( fiber.children[0] ) : fiber
		}

		static fiber_lines( fiber: $shm_hitalama_telemetry_sentry_fiber | null, prefix = '' ): string[] {
			if( !fiber ) return []
			const lines = [ `${ prefix }${ fiber.id }` ]
			for( const child of fiber.children ) lines.push( ... this.fiber_lines( child, `${ prefix }  ` ) )
			return lines
		}

		static args_snapshot( args: unknown ) {
			if( !Array.isArray( args ) ) return []
			return args.slice( 0, 8 ).map( arg => {
				if( arg === null || arg === undefined ) return arg
				if( typeof arg === 'string' ) return arg.slice( 0, 200 )
				if( typeof arg === 'number' || typeof arg === 'boolean' ) return arg
				return `<${ ( arg as any )?.constructor?.name ?? typeof arg }>`
			} )
		}

		static safe_text( value: unknown ) {
			try {
				return String( value ).slice( 0, 500 )
			} catch {
				return '<unknown fiber>'
			}
		}

		static node_name( node: Element ) {
			const names = node.getAttributeNames().filter( name => name.startsWith( 'shm_' ) || name.startsWith( 'mol_' ) )
			return names[0] || node.tagName.toLowerCase()
		}

		static capture_test( message = 'Hitalama Sentry test' ) {
			const error = new Error( message )
			error.name = 'HitalamaSentryTestError'
			return this.sdk()?.captureException?.( error ) ?? null
		}

	}

	$shm_hitalama_telemetry_sentry.view_probe_install()
	$shm_hitalama_telemetry_sentry.fail_probe_install()

}
